import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface DealMessage {
  id: string;
  deal_id: string;
  sender_profile_id: string;
  message_text: string;
  message_type: string;
  file_urls: any;
  is_read: boolean;
  read_at: string | null;
  parent_message_id: string | null;
  created_at: string;
  updated_at: string;
  sender?: {
    display_name: string;
    avatar_url: string;
    account_type: string;
  };
}

export const useDealMessages = (dealId: string | null) => {
  const [messages, setMessages] = useState<DealMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { currentUser } = useUser();

  const fetchMessages = useCallback(async () => {
    if (!dealId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('deal_messages')
        .select(`
          *,
          sender:profiles!sender_profile_id(
            display_name,
            avatar_url,
            account_type
          )
        `)
        .eq('deal_id', dealId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  const sendMessage = async (messageText: string, fileUrls: any[] = []) => {
    if (!dealId || !currentUser?.id || !messageText.trim()) return;

    setSending(true);
    
    // Create optimistic message
    const optimisticMessage: DealMessage = {
      id: `temp-${Date.now()}`,
      deal_id: dealId,
      sender_profile_id: currentUser.id,
      message_text: messageText.trim(),
      message_type: fileUrls.length > 0 ? 'file' : 'text',
      file_urls: fileUrls,
      is_read: false,
      read_at: null,
      parent_message_id: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      sender: {
        display_name: currentUser.display_name,
        avatar_url: currentUser.avatar_url || '',
        account_type: currentUser.account_type,
      },
    };

    // Add optimistically
    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const { data, error } = await supabase
        .from('deal_messages')
        .insert({
          deal_id: dealId,
          sender_profile_id: currentUser.id,
          message_text: messageText.trim(),
          message_type: fileUrls.length > 0 ? 'file' : 'text',
          file_urls: fileUrls,
        })
        .select(`
          *,
          sender:profiles!sender_profile_id(
            display_name,
            avatar_url,
            account_type
          )
        `)
        .single();

      if (error) throw error;

      // Replace optimistic message with real one
      setMessages((prev) => 
        prev.map(msg => 
          msg.id === optimisticMessage.id ? (data as DealMessage) : msg
        )
      );
    } catch (error) {
      // Remove optimistic message on error
      setMessages((prev) => 
        prev.filter(msg => msg.id !== optimisticMessage.id)
      );
      console.error('Error sending message:', error);
      throw error;
    } finally {
      setSending(false);
    }
  };

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    if (!dealId) return [];
    
    const uploadedUrls: string[] = [];
    
    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${dealId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('deal-message-files')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Error uploading file:', error);
        throw error;
      }
      
      const { data: { publicUrl } } = supabase.storage
        .from('deal-message-files')
        .getPublicUrl(data.path);
      
      uploadedUrls.push(publicUrl);
    }
    
    return uploadedUrls;
  };

  const markAsRead = async () => {
    if (!dealId || !currentUser?.id) return;

    try {
      const now = new Date().toISOString();
      await supabase
        .from('deal_messages')
        .update({ is_read: true, read_at: now })
        .eq('deal_id', dealId)
        .neq('sender_profile_id', currentUser.id)
        .eq('is_read', false);
      
      // Update local state
      setMessages(prev => 
        prev.map(msg => 
          msg.sender_profile_id !== currentUser.id && !msg.is_read
            ? { ...msg, is_read: true, read_at: now }
            : msg
        )
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const markMessageRead = async (messageId: string) => {
    if (!currentUser?.id) return;

    try {
      const now = new Date().toISOString();
      await supabase
        .from('deal_messages')
        .update({ is_read: true, read_at: now })
        .eq('id', messageId);
      
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, is_read: true, read_at: now } : msg
        )
      );
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  useEffect(() => {
    if (!dealId) return;

    fetchMessages();

    console.log(`[DealMessages] Setting up realtime for deal: ${dealId}`);

    const channel = supabase
      .channel(`deal-messages-${dealId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'deal_messages',
          filter: `deal_id=eq.${dealId}`,
        },
        async (payload) => {
          console.log('[DealMessages] Received new message:', payload);
          
          const { data: senderData } = await supabase
            .from('profiles')
            .select('display_name, avatar_url, account_type')
            .eq('id', payload.new.sender_profile_id)
            .single();

          const newMessage = {
            ...payload.new,
            sender: senderData,
          } as DealMessage;

          setMessages((prev) => {
            // Prevent duplicate messages
            if (prev.some(msg => msg.id === newMessage.id)) {
              console.log('[DealMessages] Duplicate message prevented:', newMessage.id);
              return prev;
            }
            return [...prev, newMessage];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deal_messages',
          filter: `deal_id=eq.${dealId}`,
        },
        (payload) => {
          console.log('[DealMessages] Message updated:', payload);
          
          // Update message with new read status
          setMessages((prev) => 
            prev.map(msg => 
              msg.id === payload.new.id 
                ? { ...msg, is_read: payload.new.is_read, read_at: payload.new.read_at }
                : msg
            )
          );
        }
      )
      .subscribe((status) => {
        console.log('[DealMessages] Subscription status:', status);
      });

    return () => {
      console.log(`[DealMessages] Cleaning up realtime for deal: ${dealId}`);
      supabase.removeChannel(channel);
    };
  }, [dealId, fetchMessages]);

  return {
    messages,
    loading,
    sending,
    sendMessage,
    markAsRead,
    markMessageRead,
    fetchMessages,
    uploadFiles,
  };
};
